import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import {
  Specialization_DifficultyLevel as DifficultyLevel,
  CareerRole_PathwayLevel as PathwayLevel,
} from '__generated__/graphql-types';
import type { CareerRole_MerchandisingJobCategory as JobCategory } from '__generated__/graphql-types';

import { CAREER_ROLES_COLLECTION_RECOMMENDER_CONFIGURATION_ID } from 'bundles/unified-career-academy/constants';
import CareerRolesCollectionQuery from 'bundles/unified-common/queries/CareerRolesCollection.graphql';
import type {
  CareerRolesCollectionFragment,
  CareerRolesCollectionQueryQuery as CareerRolesCollectionQueryType,
} from 'bundles/unified-common/queries/__generated__/CareerRolesCollection';
import type { CareerRolesCollectionType } from 'bundles/unified-common/types/careerRoles';

export const getPathwayLevel = (difficultyLevel?: DifficultyLevel) => {
  switch (difficultyLevel) {
    case DifficultyLevel.Beginner:
      return PathwayLevel.Beginner;
    case DifficultyLevel.Intermediate:
      return PathwayLevel.Intermediate;
    case DifficultyLevel.Advanced:
      return PathwayLevel.Advanced;
    default:
      return undefined;
  }
};

/**
 * @description Used to normalize the career roles collection by adding the proficiency level to each pathway
 */
export const normalizeCareerRolesCollection = (
  content?: CareerRolesCollectionFragment
): CareerRolesCollectionType | undefined => {
  if (!content) {
    return undefined;
  }

  const collection = {
    ...content,
    entities: content?.entities?.elements
      .map((entity) => {
        if (!entity || entity.__typename !== 'Skill_CareerRole') {
          return null;
        }

        const pathways = {
          ...entity?.pathways,
          entities: entity?.pathways?.entities?.elements
            .map((pathway) => {
              if (
                pathway?.__typename !== 'ProductCard_ProductCard' ||
                pathway?.productTypeAttributes?.__typename !== 'ProductCard_Specialization'
              ) {
                return null;
              }

              const product = pathway?.productTypeAttributes?.canonical;

              return {
                ...product,
                proficiencyLevel: getPathwayLevel(product?.difficultyLevel),
              };
            })
            .filter(Boolean),
        };

        return {
          ...entity,
          pathways,
        };
      })
      .filter(Boolean),
  };

  return collection as CareerRolesCollectionType;
};

/**
 * @description Used to get the list of career roles for the given level and job category
 */
function useCareerRolesCollection({
  ssr,
  skip,
  limit = 12,
  level,
  jobCategory: category,
  onCompleted,
}: {
  ssr: boolean;
  skip: boolean;
  limit?: number;
  level?: PathwayLevel;
  jobCategory?: JobCategory;
  onCompleted?: (data: CareerRolesCollectionQueryType) => void;
}) {
  const { data, loading } = useQuery<CareerRolesCollectionQueryType>(CareerRolesCollectionQuery, {
    variables: {
      queryCollectionId: CAREER_ROLES_COLLECTION_RECOMMENDER_CONFIGURATION_ID,
      additionalInput: {
        level: level ?? PathwayLevel.Beginner,
        ...(category ? { category } : {}),
      },
      limit,
    },
    notifyOnNetworkStatusChange: true,
    ssr,
    skip,
    context: { clientName: 'gatewayGql' },
    onCompleted,
    onError: (e) =>
      Sentry.captureException(e, {
        extra: {
          message: `Error fetching Pathway career roles collection with the recommender ID "${CAREER_ROLES_COLLECTION_RECOMMENDER_CONFIGURATION_ID}"`,
        },
      }),
  });

  return {
    data: normalizeCareerRolesCollection(data?.Collection?.queryCollection),
    loading,
  };
}

export default useCareerRolesCollection;
